import React, { useState, useCallback } from 'react';
import { api } from '../lib/api';
import { formatTemp } from '../utils/temperature';

const STATE_COLORS = { calm: '#7ecfc0', watch: '#e8c878', alert: '#e8a598', severe: '#e87878' };

export default function RightPanel({ event, events, selectedEventId, onSelectEvent, selectedRegionId, regionDetail, tempUnit }) {
  const [filter, setFilter] = useState('all');
  const [script, setScript] = useState(null);
  const [generating, setGenerating] = useState(false);

  const filteredEvents = (events || []).filter(ev => filter === 'all' || ev.origin === filter);

  const generateScript = useCallback(async () => {
    if (!event) return;
    setGenerating(true);
    try {
      const res = await api.post(`/api/nova/generate-script/event/${event.id}?output_mode=assessment&tone_mode=broadcast`);
      setScript(res.data);
    } catch {
      setScript({ script: 'Script generation unavailable.', assessment: null, knowledge_rules_applied: [] });
    }
    setGenerating(false);
  }, [event]);

  // Show region detail panel when a region is selected
  if (selectedRegionId && regionDetail) {
    const rd = regionDetail;
    const region = rd.region || {};
    const assessment = rd.assessment || {};
    const regionEvents = rd.events || [];
    const scripts = rd.recent_scripts || [];
    const aColor = STATE_COLORS[assessment.state?.toLowerCase()] || '#7ecfc0';
    const weather = region.weather;

    return (
      <div className="right-panel" data-testid="right-panel">
        <div className="detail-pane" data-testid="region-detail-pane">
          <div className="detail-header">
            <div className="detail-row1">
              <span className="badge" style={{ color: aColor, background: `${aColor}15` }} data-testid="region-detail-badge">{assessment.state || 'CALM'}</span>
              <span className="detail-ts">{region.country}</span>
            </div>
            <div className="detail-title" data-testid="region-detail-title">{region.name}</div>
            <div className="detail-cat">{region.type === 'primary' ? 'Primary Region' : 'Global Sentinel'} &middot; {region.signal_count || 0} signals</div>
          </div>

          {/* Weather Snapshot */}
          {weather && (
            <div className="detail-block">
              <div className="section-label">WEATHER</div>
              <div className="weather-snapshot" data-testid="region-weather-snapshot">
                <div className="ws-row"><span className="ws-label">Temperature</span><span className="ws-value">{formatTemp(weather.temperature_c, tempUnit)}</span></div>
                {weather.feels_like_c != null && <div className="ws-row"><span className="ws-label">Feels Like</span><span className="ws-value">{formatTemp(weather.feels_like_c, tempUnit)}</span></div>}
                {weather.humidity_pct != null && <div className="ws-row"><span className="ws-label">Humidity</span><span className="ws-value">{weather.humidity_pct}%</span></div>}
                {weather.wind_speed_kn != null && <div className="ws-row"><span className="ws-label">Wind</span><span className="ws-value">{weather.wind_speed_kn} kn</span></div>}
                {weather.precipitation_mm != null && <div className="ws-row"><span className="ws-label">Precip</span><span className="ws-value">{weather.precipitation_mm} mm</span></div>}
                {weather.pressure_hpa != null && <div className="ws-row"><span className="ws-label">Pressure</span><span className="ws-value">{Math.round(weather.pressure_hpa)} hPa</span></div>}
                {weather.weather_desc && <div className="ws-desc">{weather.weather_desc}</div>}
              </div>
            </div>
          )}

          {/* Assessment */}
          <div className="detail-block">
            <div className="section-label">NOVA ASSESSMENT</div>
            <div className="block-body assessment" style={{ borderLeftColor: `${aColor}40` }} data-testid="region-assessment">{assessment.summary}</div>
            {assessment.uncertainty && <div style={{ fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--muted)', marginTop: 4, fontStyle: 'italic' }}>{assessment.uncertainty}</div>}
          </div>

          {/* Confidence */}
          <div className="detail-block">
            <div className="conf-label">
              <span className="conf-label-text">CONFIDENCE</span>
              <span className="conf-label-val" style={{ color: aColor }}>{Math.round(assessment.confidence * 100)}%</span>
            </div>
            <div className="bar-track"><div className="bar-fill" style={{ width: `${Math.round(assessment.confidence * 100)}%`, background: `linear-gradient(90deg, ${aColor}50, ${aColor})` }} /></div>
          </div>

          {/* Intelligence Trace */}
          {assessment.knowledge_rules_applied?.length > 0 && (
            <div className="detail-block">
              <div className="section-label">INTELLIGENCE TRACE</div>
              <div className="assessment-input" data-testid="intelligence-trace">
                <div className="ai-label">KNOWLEDGE RULES</div>
                <div className="rules-applied">{assessment.knowledge_rules_applied.map((r, i) => <span className="rule-tag" key={i}>{r}</span>)}</div>
              </div>
            </div>
          )}

          {/* Subregions */}
          {region.subregions?.length > 0 && (
            <div className="detail-block">
              <div className="section-label">ANALYSIS ZONES ({region.subregions.length})</div>
              {region.subregions.map(sr => (
                <div className="ws-row" key={sr.id}>
                  <span className="ws-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: STATE_COLORS[sr.state] || '#7ecfc0', display: 'inline-block' }} />
                    {sr.name}
                  </span>
                  <span className="ws-value">{sr.weather ? formatTemp(sr.weather.temperature_c, tempUnit) : '--'}</span>
                </div>
              ))}
            </div>
          )}

          {/* Region Events */}
          {regionEvents.length > 0 && (
            <div className="detail-block">
              <div className="section-label">ACTIVE SIGNALS ({regionEvents.length})</div>
              {regionEvents.slice(0, 5).map(ev => (
                <div className="event-card" key={ev.id} onClick={() => onSelectEvent(ev.id)} style={{ cursor: 'pointer' }}>
                  <div className="ec-row1">
                    <span className="badge" style={{ color: STATE_COLORS[(ev.alert || 'CALM').toLowerCase()], background: `${STATE_COLORS[(ev.alert || 'CALM').toLowerCase()]}15` }}>{ev.alert}</span>
                  </div>
                  <div className="ec-title">{ev.title?.slice(0, 40)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Recent Scripts */}
          {scripts.length > 0 && (
            <div className="detail-block">
              <div className="section-label">RECENT SCRIPTS</div>
              {scripts.slice(0, 2).map((s, i) => (
                <div className="rs-item" key={i}>{s.generated_script?.slice(0, 120)}...</div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default: show event detail
  if (!event) return <div className="right-panel" data-testid="right-panel"><div className="no-event">awaiting signal...</div></div>;

  const alertKey = (event.alert || 'CALM').toLowerCase();
  const color = STATE_COLORS[alertKey] || '#7ecfc0';
  const confPct = Math.round((event.confidence || 0) * 100);
  const ts = event.timestamp ? (() => { try { return new Date(event.timestamp).toISOString().slice(11, 16); } catch { return ''; } })() : '';

  return (
    <div className="right-panel" data-testid="right-panel">
      <div className="detail-pane" key={event.id} data-testid="detail-pane">
        <div className="detail-header">
          <div className="detail-row1">
            <span className="badge" style={{ color, background: `${color}15` }} data-testid="detail-badge">{event.alert}</span>
            <span className="detail-ts">{ts} UTC</span>
          </div>
          <div className="detail-title" data-testid="detail-title">{event.title}</div>
          <div className="detail-cat">{event.category} &middot; {event.region}</div>
        </div>

        <div className="detail-block">
          <div className="conf-label">
            <span className="conf-label-text">CONFIDENCE</span>
            <span className="conf-label-val" style={{ color }} data-testid="detail-confidence">{confPct}%</span>
          </div>
          <div className="bar-track"><div className="bar-fill" style={{ width: `${confPct}%`, background: `linear-gradient(90deg, ${color}50, ${color})` }} /></div>
        </div>

        <div className="detail-block">
          <div className="section-label">NOVA ASSESSMENT</div>
          <div className="block-body assessment" style={{ borderLeftColor: `${color}40` }} data-testid="detail-assessment">{event.summary}</div>
        </div>

        {event.forecast && (
          <div className="detail-block">
            <div className="section-label">FORECAST</div>
            <div className="block-body" data-testid="detail-forecast">{event.forecast}</div>
          </div>
        )}

        <div className="detail-block">
          <div className="section-label">SIGNAL ORIGIN</div>
          <div className="origin-tag" style={{ color, borderColor: `${color}20` }}>{event.origin?.toUpperCase()}</div>
        </div>

        {event.sources?.length > 0 && (
          <div className="detail-block">
            <div className="section-label">EVIDENCE</div>
            <div className="sources">{event.sources.map((s, i) => <span className="src-tag" key={i} style={{ color, borderColor: `${color}20`, background: `${color}08` }}>{s}</span>)}</div>
          </div>
        )}

        <div className="detail-block">
          <div className="section-label">NOVA SCRIPT</div>
          <button className="script-gen-btn" onClick={generateScript} disabled={generating} data-testid="generate-script-btn">
            {generating ? 'GENERATING...' : 'GENERATE SCRIPT'}
          </button>
          {script && (
            <div className="script-output" data-testid="script-output">
              {script.assessment && (
                <div className="assessment-input" data-testid="assessment-transparency">
                  <div className="ai-label">STRUCTURED ASSESSMENT</div>
                  <div>State: {script.assessment.state} | Severity: {script.assessment.severity} | Confidence: {script.assessment.confidence}</div>
                  <div>Domain: {script.assessment.domain}</div>
                  {script.knowledge_rules_applied?.length > 0 && (
                    <div className="rules-applied">{script.knowledge_rules_applied.map((r, i) => <span className="rule-tag" key={i}>{r}</span>)}</div>
                  )}
                </div>
              )}
              <div className="script-text">{script.script}</div>
              <div className="script-meta">
                <span className="script-rules">{script.knowledge_rules_applied?.length || 0} rules</span>
                <span className="script-mode">{script.output_mode} / {script.tone_mode}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="event-list" data-testid="event-list">
        <div className="event-list-header">
          <div className="section-label" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>EVENTS ({filteredEvents.length})</div>
          <div className="filter-row">
            {['all', 'atmospheric', 'marine', 'seismic', 'tropical'].map(f => (
              <button key={f} className={`filter-btn${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
                {f === 'all' ? 'ALL' : f.slice(0, 4).toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="event-list-scroll">
          {filteredEvents.map(ev => {
            const evColor = STATE_COLORS[(ev.alert || 'CALM').toLowerCase()] || '#7ecfc0';
            const isSelected = ev.id === selectedEventId;
            const evTs = ev.timestamp ? (() => { try { return new Date(ev.timestamp).toISOString().slice(11, 16); } catch { return ''; } })() : '';
            return (
              <div key={ev.id} className={`event-card${isSelected ? ' selected' : ''}`} style={isSelected ? { borderLeftColor: evColor } : {}} onClick={() => { onSelectEvent(ev.id); setScript(null); }} data-testid={`event-card-${ev.id}`}>
                <div className="ec-row1">
                  <span className="badge" style={{ color: evColor, background: `${evColor}15` }}>{ev.alert || 'CALM'}</span>
                  <span className="detail-ts">{evTs}</span>
                </div>
                <div className="ec-title">{ev.title?.slice(0, 50)}</div>
                <div className="ec-cat">{ev.category} &middot; {ev.region?.slice(0, 35)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
